import useSWR from 'swr'
import { useState, useEffect } from 'react'
import { Spinner } from '@contentful/f36-spinner'
import { createClient } from 'contentful'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import Header from './Header'
import Footer from './Footer'
import Content from './Content'
import Post from './Post'

const client = createClient({
  space: process.env.CONTENTFUL_SPACE_ID,
  accessToken: process.env.CONTENTFUL_DELIVERY_TOKEN,
})

const ITEMS_PER_PAGE = 20

const fetcher = async (key, skip = 0) => {
  const entryItems = await client.getEntries({
    content_type: 'article',
    order: '-fields.date',
    limit: ITEMS_PER_PAGE,
    skip: skip,
    'fields.category.sys.id': '3yk6ZcPzfN9tGdhyGjwrEN',
  })

  const entries = entryItems.items.map((entry) => {
    const { fields } = entry
    return {
      title: fields.title,
      body: fields.body,
      date: fields.date,
      slug: fields.slug,
    }
  })

  return {
    entries,
    total: entryItems.total,
  }
}

const ScrollHandler = () => {
  const location = useLocation()

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'auto'
    document.body.style.scrollBehavior = 'auto'
    window.history.scrollRestoration = 'manual'
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [location])

  return null
}

const Shell = ({ children }) => (
  <div className="flash-news-container">
    <a href="#main" className="skip-link">تخطّي إلى المحتوى</a>
    <Header />
    <main id="main" className="flash-main">
      {children}
    </main>
    <Footer />
  </div>
)

const App = () => {
  const [page, setPage] = useState(0)
  const { data, error } = useSWR(
    ['contentful', page * ITEMS_PER_PAGE],
    ([key, skip]) => fetcher(key, skip),
  )
  const [allEntries, setAllEntries] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  if (error) {
    console.log(error)
    return (
      <Shell>
        <div className="container container--narrow">
          <p className="flash-error">تعذّر تحميل المحتوى. يرجى المحاولة لاحقًا.</p>
        </div>
      </Shell>
    )
  }

  if (!data && page === 0) {
    return (
      <Shell>
        <div className="flash-loading" role="status">
          <Spinner size="large" />
        </div>
      </Shell>
    )
  }

  if (data && data.entries.length > 0 && allEntries.length === 0) {
    setAllEntries(data.entries)
    setHasMore(data.entries.length < data.total)
  }

  const loadMore = async () => {
    if (loading || !hasMore) return

    setLoading(true)
    const nextPage = page + 1
    const newData = await fetcher('contentful', nextPage * ITEMS_PER_PAGE)

    setPage(nextPage)
    setAllEntries((prev) => [...prev, ...newData.entries])
    setHasMore(allEntries.length + newData.entries.length < newData.total)
    setLoading(false)
  }

  return (
    <Router>
      <ScrollHandler />
      <Shell>
        <Routes>
          <Route
            path="/"
            element={
              <Content
                entries={allEntries}
                onLoadMore={loadMore}
                loading={loading}
                hasMore={hasMore}
              />
            }
          />
          <Route path="/post/:id" element={<Post entries={allEntries} />} />
        </Routes>
      </Shell>
    </Router>
  )
}

export default App
